import { apiGet, apiPost, apiPut, apiDelete } from '~/utils/api'

/**
 * Cart item response (matches backend CartItemResponse)
 */
export interface CartItemResponse {
  id: string
  shopProductId: string
  inventoryItemId: string | null
  title: string
  titleTranslations: Record<string, string> | null
  /** null for accessory items (counted-stock products have no condition grade) */
  conditionGrade: string | null
  color: string | null
  storage: string | null
  salePrice: number
  regularPrice: number
  originalPrice: number | null
  weeklyDealId: string | null
  weeklyDealQuantity: number
  lineTotal: number
  quantity: number
  images: string[]
  availableStock: number
  batteryHealth: number | null
}

/**
 * Cart response (matches backend CartResponse)
 */
export interface CartResponse {
  items: CartItemResponse[]
  itemCount: number
  subtotal: number
  shipping: number
  total: number
  /** false when any actually allocated special-offer unit forbids whole-order coupon stacking */
  couponAllowed: boolean
  // 仅 preview-coupon 返回时填充
  appliedCouponCode?: string | null
  discount?: number | null
  discountedTotal?: number | null
}

/**
 * Cart API composable with global shared state
 */
export function useCartAPI() {
  const cart = useState<CartResponse | null>('midigi-cart', () => null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const cartItems = computed(() => cart.value?.items ?? [])
  const itemCount = computed(() => cart.value?.itemCount ?? 0)
  const subtotal = computed(() => cart.value?.subtotal ?? 0)
  const shipping = computed(() => cart.value?.shipping ?? 0)
  const total = computed(() => cart.value?.total ?? 0)
  const couponAllowed = computed(() => cart.value?.couponAllowed ?? true)
  const isEmpty = computed(() => itemCount.value === 0)

  async function fetchCart(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      cart.value = await apiGet<CartResponse>('/api/app/v1/cart')
    } catch (e) {
      // Silently fail if not logged in — show empty cart
      cart.value = null
    } finally {
      loading.value = false
    }
  }

  async function addItem(shopProductId: string, quantity: number = 1, inventoryItemId?: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const body: Record<string, any> = { shopProductId, quantity }
      if (inventoryItemId) body.inventoryItemId = inventoryItemId
      cart.value = await apiPost<CartResponse>('/api/app/v1/cart/items', body)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function updateItem(itemId: string, quantity: number): Promise<void> {
    loading.value = true
    error.value = null

    try {
      cart.value = await apiPut<CartResponse>(`/api/app/v1/cart/items/${itemId}`, {
        quantity,
      })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function removeItem(itemId: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      cart.value = await apiDelete<CartResponse>(`/api/app/v1/cart/items/${itemId}`)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function clearCart(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      cart.value = await apiDelete<CartResponse>('/api/app/v1/cart')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  function resetCart(): void {
    cart.value = null
  }

  /**
   * 预览优惠券（只读，不改共享购物车状态）。返回含折扣字段的 CartResponse。
   * 无效时后端抛错（error.coupon.*），由调用方处理。
   */
  async function previewCoupon(code: string): Promise<CartResponse> {
    return await apiPost<CartResponse>('/api/app/v1/cart/preview-coupon', { code })
  }

  return {
    cart,
    loading,
    error,
    cartItems,
    itemCount,
    subtotal,
    shipping,
    total,
    couponAllowed,
    isEmpty,
    fetchCart,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    resetCart,
    previewCoupon,
  }
}
